import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateField } from "@/components/DateTimeField";
import { api, formatApiErrorDetail } from "@/lib/api";

const STAGES = ["Quote", "Template", "Fabrication"];

// Turns a material hold into a job; the held slab pieces come along already reserved to it.
export const ConvertHoldDialog = ({ hold, open, onClose }) => {
  const navigate = useNavigate();
  const [project, setProject] = useState(hold.project_name || "");
  const [stage, setStage] = useState("Template");
  const [installDate, setInstallDate] = useState(hold.install_date || "");
  const [summary, setSummary] = useState(hold.comments || "");
  const [busy, setBusy] = useState(false);
  const pieces = (hold.piece_ids || []).length;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reservations/${hold.id}/convert-to-job`, {
        project_name: project,
        stage,
        install_date: installDate || null,
        summary,
      });
      toast.success(
        `Job ${data.job.code} created${pieces ? ` — ${pieces} piece${pieces === 1 ? "" : "s"} reserved to it` : ""}`,
      );
      onClose(data);
      navigate(`/projects/${data.job.id}?tab=field`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not convert this hold",
      );
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="convert-hold-dialog"
        className="max-w-md border-zinc-800 bg-zinc-900"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase size={18} weight="bold" className="text-indigo-300" />
            Convert hold to a job
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {hold.tag} · {hold.customer_name} — a job is opened for this
            customer with {pieces ? `the ${pieces} held piece${pieces === 1 ? "" : "s"}` : "the held material"}{" "}
            linked and reserved to it. The hold moves to Fabricating.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Project name
            </Label>
            <Input
              data-testid="convert-hold-project"
              value={project}
              onChange={(e) => setProject(e.target.value)}
              className="h-11 border-zinc-800 bg-zinc-950"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Starts at stage
              </Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger
                  data-testid="convert-hold-stage"
                  className="h-11 border-zinc-800 bg-zinc-950"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {STAGES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Install date
              </Label>
              <DateField
                testid="convert-hold-install-date"
                value={installDate}
                onChange={setInstallDate}
                placeholder="Not set yet"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Job summary
            </Label>
            <Textarea
              data-testid="convert-hold-summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              className="border-zinc-800 bg-zinc-950"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              data-testid="convert-hold-cancel"
              variant="outline"
              onClick={() => onClose()}
              className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              data-testid="convert-hold-submit"
              disabled={busy}
              onClick={submit}
              className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Briefcase size={14} weight="bold" className="mr-1.5" />
              {busy ? "Creating…" : "Create job"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
